import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Stack, Typography, TextField, Alert, CircularProgress, MenuItem } from "@mui/material";
import { Fingerprint, Shield, HourglassEmpty, TaskAlt, Block } from "@mui/icons-material";

import { enterWebauthnFlow, resetWebauthnFlow } from "../../redux/actions/auth";
import { NEXT_STEP, LABEL_MAX_LEN, DEVICE_TYPES } from "./config";
import { detectDeviceType } from "./api/webauthnApi";
import { getRegRequestId, setRegRequestId, clearRegRequestId } from "./regRequestStore";
import { usePreauthExpiryGuard } from "./usePreauthExpiryGuard";
import { getDeviceFingerprint } from "./deviceFingerprint";
import { screenForNextStep, FLOW_SCREEN } from "./state/loginFlowMachine";
import { useWebauthnSupport } from "./hooks/useWebauthnSupport";
import { useAuthenticateCeremony } from "./hooks/useAuthenticateCeremony";
import { useRegisterCeremony } from "./hooks/useRegisterCeremony";
import { useRegistrationStatus } from "./hooks/useRegistrationStatus";
import { webauthnApi } from "./api/webauthnApi";
import { FlowShell, FlowHeader, PrimaryButton, SecondaryButton, humanizeError, ORANGE } from "./components/ui";
import WebauthnUnsupported from "./components/WebauthnUnsupported";

/**
 * Pre-auth WebAuthn flow. Rendered by the Login screen while a pre-auth state
 * exists in redux (auth.webauthn). Drives assertion / enrollment / pending
 * screens off the server-issued next_step. Finalizes the session via the
 * ceremony hooks (finalizeWebauthnLogin), after which isAuthenticated flips
 * and the router leaves this view.
 */
export default function WebauthnFlow() {
  const dispatch = useDispatch();
  const flow = useSelector((s) => s.auth.webauthn);
  const { supported, platformAvailable } = useWebauthnSupport();

  // Warm the fingerprint early so the await before verify/request is instant.
  useEffect(() => {
    getDeviceFingerprint();
  }, []);

  const cancel = () => dispatch(resetWebauthnFlow());

  if (!flow) return null;
  if (!supported) return <WebauthnUnsupported onBack={cancel} />;

  const screen = screenForNextStep(flow.next_step);

  if (screen === FLOW_SCREEN.AUTHENTICATE)
    return <AuthenticateScreen onEnroll={() => dispatch(enterWebauthnFlow({ ...flow, next_step: NEXT_STEP.REGISTRATION_REQUIRED }))} onCancel={cancel} />;

  if (screen === FLOW_SCREEN.ENROLL)
    return <EnrollScreen flow={flow} platformAvailable={platformAvailable} onCancel={cancel} />;

  if (screen === FLOW_SCREEN.PENDING)
    return <PendingScreen flow={flow} onCancel={cancel} />;

  return (
    <FlowShell>
      <FlowHeader icon={<Block sx={{ fontSize: 30 }} />} title="Unexpected state" subtitle="Please sign in again." />
      <SecondaryButton onClick={cancel}>Back to sign in</SecondaryButton>
    </FlowShell>
  );
}

// ---- Assertion (known browser) ----
function AuthenticateScreen({ onEnroll, onCancel }) {
  const ceremony = useAuthenticateCeremony();

  return (
    <FlowShell>
      <FlowHeader
        icon={<Fingerprint sx={{ fontSize: 30 }} />}
        title="Confirm it's you"
        subtitle="Use this browser's fingerprint, Face ID, Windows Hello, or security key to finish signing in."
      />
      {ceremony.isError && (
        <Alert severity="error" sx={{ borderRadius: "10px", fontSize: 13 }}>
          {humanizeError(ceremony.error)}
        </Alert>
      )}
      <PrimaryButton loading={ceremony.isLoading} onClick={() => ceremony.mutate()}>
        Verify
      </PrimaryButton>
      {/* This browser may not hold a credential yet (get() fails with no match);
          always let the user pivot to enrolling it. */}
      <SecondaryButton onClick={onEnroll}>Enroll this browser instead</SecondaryButton>
      <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
    </FlowShell>
  );
}

// ---- Enrollment: request (Phase 1) or complete attestation (Phase 3) ----
function EnrollScreen({ flow, platformAvailable, onCancel }) {
  const dispatch = useDispatch();

  const [label, setLabel] = useState("");
  const [deviceType, setDeviceType] = useState(detectDeviceType());
  const [reqError, setReqError] = useState(null);
  const [requesting, setRequesting] = useState(false);
  // "Finish setup" only renders once register/status confirms an approved
  // request that belongs to THIS browser. A login-supplied `registration_ready`
  // is just a claim — it must be verified, never trusted directly.
  const [confirmedReady, setConfirmedReady] = useState(false);
  // Until we've checked for an existing request, don't show the request form —
  // re-logging in with a pending/approved request must not create a duplicate.
  const [checking, setChecking] = useState(true);
  const ceremony = useRegisterCeremony();
  const guardPreauth = usePreauthExpiryGuard();

  useEffect(() => {
    // "Register again" after a rejection: skip the status re-check (which would
    // just bounce back to the rejected Pending screen) and show the request
    // form. submitRequest overwrites the stored id with the new request's id.
    if (flow.force_enroll) {
      setChecking(false);
      return;
    }
    // Only a prior register/request on THIS browser writes the id (see
    // submitRequest). If there's none, this device hasn't requested → show the
    // request form — even when login claimed registration_ready (that can be an
    // account-scoped approval from another device). Never adopt an id from the
    // login/status response.
    const storedId = getRegRequestId(flow.analyst_id);
    if (!storedId) {
      setChecking(false);
      return;
    }
    let alive = true;
    // Status is READ-ONLY for storage: we pass our stored id and route on the
    // result, but we never write localStorage here.
    webauthnApi
      .registerStatus(flow.preauth_ticket, storedId)
      .then((s) => {
        if (!alive || !s) return;
        if (s.status === "pending" || s.status === "rejected") {
          dispatch(
            enterWebauthnFlow({ ...flow, next_step: NEXT_STEP.REGISTRATION_PENDING, request_id: storedId })
          );
        } else if (s.status === "approved") {
          setConfirmedReady(true);
          setChecking(false);
        } else {
          // "none" / "completed" → the id is stale or consumed (e.g. the
          // credential was revoked). Drop it so this device can request afresh.
          clearRegRequestId(flow.analyst_id);
          setChecking(false);
        }
      })
      .catch((e) => {
        if (!alive) return;
        // Pre-auth ticket expired → bounce to login.
        if (guardPreauth(e)) return;
        // Unmatched/unknown request id → drop it so this device re-registers.
        // Leave it on transient errors (network/5xx) to not lose a valid request.
        if (e && e.response && e.response.status === 404) {
          clearRegRequestId(flow.analyst_id);
        }
        setChecking(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitRequest = async () => {
    setReqError(null);
    setRequesting(true);
    try {
      const fingerprint = await getDeviceFingerprint();
      const res = await webauthnApi.registerRequest(
        label.trim() || undefined,
        deviceType,
        fingerprint,
        flow.preauth_ticket
      );
      // Overwrite any prior id — a new request always replaces the old one.
      if (res && res.request_id) setRegRequestId(flow.analyst_id, res.request_id);
      dispatch(
        enterWebauthnFlow({
          ...flow,
          next_step: NEXT_STEP.REGISTRATION_PENDING,
          request_id: res && res.request_id,
          // One-shot flag — clear it so a later approval reaches Finish setup
          // instead of re-showing the request form.
          force_enroll: false,
        })
      );
    } catch (e) {
      if (!guardPreauth(e)) setReqError(humanizeError(e));
    } finally {
      setRequesting(false);
    }
  };

  // `confirmedReady` is set only after register/status confirms an approved
  // request for THIS browser's stored id — so it safely wins over `checking`.
  if (confirmedReady) {
    return (
      <FlowShell>
        <FlowHeader
          icon={<Shield sx={{ fontSize: 30 }} />}
          title="Finish setting up this browser"
          subtitle="Your request was approved. Create a secure credential to complete enrollment."
        />
        {ceremony.isError && (
          <Alert severity="error" sx={{ borderRadius: "10px", fontSize: 13 }}>
            {humanizeError(ceremony.error)}
          </Alert>
        )}
        <PrimaryButton
          loading={ceremony.isLoading}
          onClick={() => ceremony.mutate({ requestId: getRegRequestId(flow.analyst_id), label: label.trim() || undefined })}
        >
          Finish setup
        </PrimaryButton>
        <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
      </FlowShell>
    );
  }

  if (checking) {
    return (
      <FlowShell>
        <FlowHeader icon={<Shield sx={{ fontSize: 30 }} />} title="Checking enrollment…" />
        <Stack alignItems="center" py={1}>
          <CircularProgress size={22} sx={{ color: ORANGE }} />
        </Stack>
      </FlowShell>
    );
  }

  return (
    <FlowShell>
      <FlowHeader
        icon={<Shield sx={{ fontSize: 30 }} />}
        title="Enroll this browser"
        subtitle="This browser isn't approved yet. Request access — an administrator will review it before you can sign in here."
      />
      {platformAvailable === false && (
        <Alert severity="warning" sx={{ borderRadius: "10px", fontSize: 13 }}>
          No built-in authenticator detected — you'll be asked for a security key.
        </Alert>
      )}
      {reqError && (
        <Alert severity="error" sx={{ borderRadius: "10px", fontSize: 13 }}>
          {reqError}
        </Alert>
      )}
      <Stack gap={0.75}>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "var(--text-2)" }}>
          Name this browser (optional)
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="e.g. Office Chrome"
          value={label}
          onChange={(e) => setLabel(e.target.value.slice(0, LABEL_MAX_LEN))}
          InputProps={{
            sx: {
              borderRadius: "10px",
              fontSize: 14,
              height: 44,
              backgroundColor: "var(--surface-1)",
              "&.Mui-focused fieldset": { borderColor: ORANGE + " !important" },
            },
          }}
        />
      </Stack>
      <Stack gap={0.75}>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "var(--text-2)" }}>
          Device type
        </Typography>
        <TextField
          select
          fullWidth
          size="small"
          value={deviceType}
          onChange={(e) => setDeviceType(e.target.value)}
          InputProps={{
            sx: {
              borderRadius: "10px",
              fontSize: 14,
              height: 44,
              backgroundColor: "var(--surface-1)",
              "&.Mui-focused fieldset": { borderColor: ORANGE + " !important" },
            },
          }}
        >
          {DEVICE_TYPES.map((d) => (
            <MenuItem key={d.value} value={d.value} sx={{ fontSize: 14 }}>
              {d.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <PrimaryButton loading={requesting} onClick={submitRequest}>
        Request access
      </PrimaryButton>
      <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
    </FlowShell>
  );
}

// ---- Pending admin approval ----
function PendingScreen({ flow, onCancel }) {
  const dispatch = useDispatch();
  const { data, isLoading } = useRegistrationStatus(true);

  useEffect(() => {
    if (!data) return;
    if (data.status === "approved") {
      dispatch(
        enterWebauthnFlow({
          ...flow,
          next_step: NEXT_STEP.REGISTRATION_READY,
          // Keep this device's own request id — never adopt one from status.
          request_id: flow.request_id || data.request_id,
        })
      );
    }
  }, [data, dispatch, flow]);

  const rejected = data && data.status === "rejected";

  return (
    <FlowShell>
      <FlowHeader
        icon={rejected ? <Block sx={{ fontSize: 30 }} /> : <HourglassEmpty sx={{ fontSize: 30 }} />}
        title={rejected ? "Request rejected" : "Waiting for approval"}
        subtitle={
          rejected
            ? data.reject_reason || "An administrator rejected this enrollment request."
            : "An administrator needs to approve this browser. You can keep this page open — it updates automatically."
        }
      />
      {!rejected && (
        <Stack direction="row" alignItems="center" justifyContent="center" gap={1} sx={{ py: 1 }}>
          {isLoading ? (
            <CircularProgress size={18} sx={{ color: ORANGE }} />
          ) : (
            <TaskAlt sx={{ fontSize: 18, color: "var(--text-2)" }} />
          )}
          <Typography sx={{ fontSize: 13, color: "var(--text-2)" }}>
            Status: {data ? data.status : "checking…"}
          </Typography>
        </Stack>
      )}
      {rejected && (
        <PrimaryButton
          onClick={() =>
            dispatch(
              enterWebauthnFlow({ ...flow, next_step: NEXT_STEP.REGISTRATION_REQUIRED, force_enroll: true })
            )
          }
        >
          Register again
        </PrimaryButton>
      )}
      <SecondaryButton onClick={onCancel}>Back to sign in</SecondaryButton>
    </FlowShell>
  );
}
